// Path: apps/0001-estata.com/frontend/src/hooks/useKeyboardViewportLock.ts
// v1.2.1 - Im Anmeldeablauf gar kein Eingriff mehr, auch kein scrollTo (Tauziehen = Zittern, 10.10.2026).
// v1.2.0 - Im Anmeldeablauf (html.auth-lock) greift die Sperre nicht mehr ein - dort ist die Seite starr;
//          ihr Eingreifen beim zweiten Fokus (scrollY != 0) war das Federn.
// v1.1.0 - Festnageln BEVOR die Tastatur aufgeht (Inhaber, 10.10.2026: "geht auch der Bildschirm nach oben,
//          aber senkt sich dann langsam wieder ab. Aber er soll sich nicht bewegen"): v1.0.0 reagierte erst
//          auf die Groessenaenderung des Ausschnitts - da hatte iOS die Seite schon verschoben, und das
//          Zurueckziehen sah aus wie ein Absenken. Jetzt wird beim Fokus auf ein Feld (focusin) sofort
//          der Body festgesetzt (position: fixed, volle Hoehe): es gibt nichts mehr, was iOS scrollen
//          koennte. Die Seite behaelt ihre volle Hoehe - schwarz bleibt schwarz, auch hinter der
//          Tastatur (v1.0.0 kuerzte die Seite, darunter schimmerte die Startseite durch).
// v1.0.0 - Die Tastatur darf die Seite nicht nach oben ziehen (global).

import { useEffect } from 'react';

const FIELD = 'input, textarea, select, [contenteditable="true"]';

/** Haelt die Seite fest, solange ein Eingabefeld den Fokus hat (Tastatur offen). */
export function useKeyboardViewportLock(): void {
  useEffect(() => {
    const root = document.documentElement;
    const body = document.body;
    let locked = false;
    let scrollY = 0;
    let releaseTimer: number | undefined;

    const lock = () => {
      if (locked) return;
      scrollY = window.scrollY;
      body.style.position = 'fixed';
      body.style.top = `${-scrollY}px`;
      body.style.left = '0';
      body.style.right = '0';
      body.style.width = '100%';
      body.style.overflow = 'hidden';
      root.style.overflow = 'hidden';
      locked = true;
    };
    const unlock = () => {
      if (!locked) return;
      body.style.position = '';
      body.style.top = '';
      body.style.left = '';
      body.style.right = '';
      body.style.width = '';
      body.style.overflow = '';
      root.style.overflow = '';
      locked = false;
      window.scrollTo(0, scrollY);
    };
    const onFocusIn = (e: FocusEvent) => {
      const target = e.target as Element | null;
      if (!target || !target.matches?.(FIELD)) return;
      // Im Anmeldeablauf ist die Seite ohnehin starr (html.auth-lock). Hier NICHT eingreifen: beim zweiten
      // Fokus war window.scrollY nach dem Einfahren der Tastatur nicht mehr 0, lock() setzte den Body um
      // diesen Rest versetzt fest und keepTop zog ihn zurueck - das war das "Federn" (Inhaber, 10.10.2026).
      // Kein scrollTo hier: das zog gegen die iOS-Verschiebung UND den Ausgleich in LoginFlow - Zittern.
      if (root.classList.contains('auth-lock')) return;
      if (releaseTimer) { window.clearTimeout(releaseTimer); releaseTimer = undefined; }
      lock();
    };
    const onFocusOut = () => {
      // Kurz warten: beim Wechsel von Feld zu Feld kommt sofort das naechste focusin.
      if (releaseTimer) window.clearTimeout(releaseTimer);
      releaseTimer = window.setTimeout(() => {
        const active = document.activeElement;
        if (!active || !active.matches?.(FIELD)) unlock();
      }, 80);
    };
    // Sollte iOS trotzdem schieben (Feld unter der Tastatur): sofort zurueck, nicht erst nach dem Loslassen.
    const keepTop = () => { if (locked && window.scrollY !== 0) window.scrollTo(0, 0); };

    document.addEventListener('focusin', onFocusIn);
    document.addEventListener('focusout', onFocusOut);
    window.addEventListener('scroll', keepTop);
    return () => {
      document.removeEventListener('focusin', onFocusIn);
      document.removeEventListener('focusout', onFocusOut);
      window.removeEventListener('scroll', keepTop);
      if (releaseTimer) window.clearTimeout(releaseTimer);
      unlock();
    };
  }, []);
}
